import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { TabService } from '../../../../shared/tab.service';
import { markProjectSearchMilestone, startProjectSearchJourney } from '../../../../shared/project-search-performance';
import {
  ProjectSearchCatalog,
  ProjectSearchResult,
  ProjectSearchService,
} from '../../../../shared/project-search.service';
import {
  MAX_NAME_MATCHES,
  ProjectSearchEntity,
  ProjectSearchFile,
  ProjectSearchHit,
  ProjectSearchMatch,
  ProjectSearchPage,
  TextSegment,
  displaySheetId,
  entityLabel,
  entityName,
  groupByPage,
  markTerms,
  matchFiles,
  matchSheets,
  previewCommands,
  queryTerms,
  transcriptExcerpt,
} from '../../../../shared/project-search-results';

/**
 * What a row stands for. Each kind has its own icon, so a person can tell a whole sheet from a
 * file, from words found on a sheet and from an object drawn on it, such as a room (Issue #3212).
 * Code sections are to come (#2750 follow-ups).
 */
export type ProjectSearchRowKind = 'sheet' | 'file' | 'text' | 'object';

/** One row of the results panel, prepared once per search so the template does no work. */
export interface ProjectSearchRow {
  kind: ProjectSearchRowKind;
  /** The icon of the row's kind. */
  icon: RowIcon;
  /** The text hit, for a `text` row. */
  hit?: ProjectSearchHit;
  /** Where the row opens, for a `sheet` or `file` row. */
  target?: { fileId: string; pageNumber: number };
  /** Position in keyboard order, across all sections. */
  index: number;
  id: string;
  segments: TextSegment[];
  /** A sheet row's page number, shown in the circle on its icon. */
  pageNumber?: number;
  /** A sheet row's file, shown on its second line after a PDF icon. */
  fileName?: string;
  /** A second line in words: a file's page count, or where a text row's words were read from. */
  detail: string;
  /** How it matched, in words; empty for exact matches, where every row matched the same way. */
  matchLabel: string;
  /** The label's tooltip: which of the two searches found the passage. */
  matchTooltip: string;
  related: boolean;
  /**
   * For the engineering team, in power-user mode only (Issue #3077): the passage's rank in each
   * search, such as "words #1 · meaning #3". Empty otherwise.
   */
  diagnostic: string;
}

/** The text rows found on one page. */
export interface ProjectSearchGroup {
  key: string;
  headingId: string;
  /** The sheet ID and title, such as "E1.8 · UNIT TYPE D1 ELECTRICAL", or the page when unnamed. */
  heading: string;
  /** Shown in the circle on the sheet icon. */
  pageNumber: number;
  /** The file, such as "2002-040168-000-TM Plans Building F.pdf"; empty until the names load. */
  fileName: string;
  rows: ProjectSearchRow[];
}

/** A Material icon and its font set; an empty set is the app's default Material Icons font. */
export interface RowIcon {
  name: string;
  fontSet: string;
}

/**
 * The icon of each kind of row (Issue #3042). Sheets and files use the outlined Material Symbols
 * of the project contents tree, so a sheet looks the same in search as in the drawer.
 */
export const ROW_ICONS: Record<ProjectSearchRowKind, RowIcon> = {
  sheet: { name: 'draft', fontSet: 'material-symbols-outlined' },
  file: { name: 'picture_as_pdf', fontSet: 'material-symbols-outlined' },
  text: { name: 'format_quote', fontSet: '' },
  object: { name: 'crop_free', fontSet: '' },
};

/** The icon of each kind of plan object (Issue #3212); any other kind has the `object` icon. */
export const ENTITY_ICONS: Readonly<Record<string, RowIcon>> = {
  sheet: ROW_ICONS.sheet,
  drawing: { name: 'architecture', fontSet: '' },
  unit: { name: 'apartment', fontSet: '' },
  room: { name: 'meeting_room', fontSet: '' },
  fixture: { name: 'bathtub', fontSet: '' },
  'note-block': { name: 'notes', fontSet: '' },
  schedule: { name: 'table_chart', fontSet: '' },
  detail: { name: 'zoom_in', fontSet: '' },
};

/** Where a text row's words came from, as its second line says. */
const SOURCE_LABELS = {
  transcript: 'From the page transcript',
  'text-layer': 'Text on the sheet',
  ocr: 'Recognised text (OCR) on the sheet',
  sheet: 'Text on the sheet',
  object: 'Object on the sheet',
};

/** Shown while exact search reads every page, which takes seconds to half a minute. */
export const EXACT_SEARCH_WAIT =
  'This project has no search index yet, so this reads the text of every page. On a large project that can take half a minute.';

/**
 * A chip only where a reader needs it (Issue #3077): a passage found by meaning alone may not hold
 * their words, so it says "Related". Matching their words is what a result is expected to do, so
 * those passages carry no chip.
 */
const MATCH_LABELS: Record<ProjectSearchMatch, string> = {
  'words-and-meaning': '',
  words: '',
  meaning: 'Related',
  exact: '',
};

const MATCH_TOOLTIPS: Record<ProjectSearchMatch, string> = {
  'words-and-meaning': '',
  words: '',
  meaning: 'Found by the meaning search: similar, but it may not contain your words',
  exact: '',
};

/** Gives keyboard focus or pointer hover a chance to choose the one page warmed for this search. */
const PREFETCH_SETTLE_DELAY_MS = 150;

/**
 * The search field in the project top app bar (Issue #2750). It searches every page of the
 * current project: Enter runs the search, a panel lists the results grouped by page, and choosing
 * one opens the page in the Preview with the passage highlighted, through the existing highlight
 * deep link, so the address can be copied and shared.
 *
 * Ctrl+F keeps its own job: exact find within the open file, in the Preview.
 */
@Component({
  selector: 'app-project-search',
  imports: [NgTemplateOutlet, OverlayModule, MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './project-search.component.html',
  styleUrls: ['./project-search.component.scss'],
})
export class ProjectSearchComponent implements OnInit, OnChanges, OnDestroy {
  private searchService = inject(ProjectSearchService);
  private router = inject(Router);
  private changeDetector = inject(ChangeDetectorRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private tabs = inject(TabService);

  /** The project to search. Without one the field is not shown. */
  @Input() projectId: string | null = null;

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchField') private searchField?: ElementRef<HTMLElement>;

  query = '';
  status: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  result: ProjectSearchResult | null = null;
  /** Sheets whose title or ID names the query, shown at once while the text search runs. */
  sheetRows: ProjectSearchRow[] = [];
  sheetTotal = 0;
  /** Files whose name names the query. */
  fileRows: ProjectSearchRow[] = [];
  /** Text found on the pages, grouped by page. */
  groups: ProjectSearchGroup[] = [];
  /** Every row in keyboard order: sheets, files, then text. */
  rows: ProjectSearchRow[] = [];
  /** Said under the progress bar while a slow search runs. */
  waitMessage = '';
  /** The panel's width in pixels: the field's, so the panel reads as the field's dropdown. */
  panelWidth = 0;
  readonly icons = ROW_ICONS;
  activeIndex = -1;
  errorMessage = '';
  panelOpen = false;
  /** At phone width the field is hidden behind a search button until this is set. */
  expanded = false;
  /** The row whose page is being opened, while its highlight is prepared. */
  opening: ProjectSearchRow | null = null;

  readonly listboxId = 'project-search-results';
  readonly panelPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
  ];

  /** Where focus was before `/` moved it here, so Escape can give it back. */
  private returnFocus: HTMLElement | null = null;
  private catalog: ProjectSearchCatalog | null = null;
  /** The catalog being loaded for the current project, started when the field is first used. */
  private catalogLoad: Promise<ProjectSearchCatalog | null> | null = null;
  /** Each search gets a number; a reply to an older search is dropped. */
  private generation = 0;
  private destroyed = false;
  private prefetchInFlight = false;
  private prefetchChosen = false;
  private prefetchCandidate: ProjectSearchRow | null = null;
  private prefetchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onWindowKeydownListener = (event: KeyboardEvent): void => this.onDocumentKeydown(event);

  ngOnInit(): void {
    // The window capture phase with passive: false, as the Preview does: Zone.js folds document
    // keydown listeners into one existing passive callback, where preventDefault is ignored and
    // the "/" would be typed into the field.
    window.addEventListener('keydown', this.onWindowKeydownListener, { capture: true, passive: false });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.generation++;
    this.cancelPrefetchTimer();
    window.removeEventListener('keydown', this.onWindowKeydownListener, { capture: true });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.reset();
    }
  }

  /** `/` focuses the field, unless the person is typing somewhere else. */
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return;
    if (!this.projectId || isTypingTarget(event.target)) return;
    event.preventDefault();
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // At once, inside the key press: letters typed straight after the "/" must land in the field.
    this.expand();
  }

  /** Shows the field (at phone width it is hidden behind a button) and puts the cursor in it. */
  expand(): void {
    this.loadCatalog();
    this.expanded = true;
    this.changeDetector.detectChanges();
    this.searchInput?.nativeElement.focus();
    this.searchInput?.nativeElement.select();
  }

  onInput(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
    this.activeIndex = -1;
  }

  onFocus(): void {
    // Start loading the sheet and file names now, so they are ready when Enter is pressed.
    this.loadCatalog();
    if (this.result || this.status === 'error') this.openPanel();
  }

  /** Keeps the panel as wide as the field when the window changes size. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.panelOpen) this.panelWidth = this.fieldWidth();
  }

  onInputKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.panelOpen && this.activeIndex >= 0 && this.rows[this.activeIndex]) {
          void this.open(this.rows[this.activeIndex]);
        } else {
          void this.search();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.dismiss();
        break;
    }
  }

  /**
   * Runs the search for the current query. Sheet and file names are matched at once from the
   * plan, so they show while the server searches the pages' text.
   */
  async search(): Promise<void> {
    const query = this.query.trim();
    const projectId = this.projectId;
    if (!query || !projectId) return;
    const generation = ++this.generation;
    const current = (): boolean => !this.destroyed && generation === this.generation;
    this.status = 'loading';
    this.errorMessage = '';
    this.waitMessage = '';
    this.result = null;
    this.groups = [];
    this.sheetRows = [];
    this.fileRows = [];
    this.rows = [];
    this.activeIndex = -1;
    this.resetPrefetch();
    this.openPanel();
    this.changeDetector.markForCheck();
    // Sheet and file names show as soon as they are known; the text results do not wait for them,
    // and when the names arrive after the text, the text's headings are named again.
    void this.loadCatalog()?.then(catalog => {
      if (!current() || !catalog) return;
      // Rows are renumbered; keep the person's place if they have started moving through them.
      const active = this.rows[this.activeIndex]?.hit;
      this.catalog = catalog;
      this.showNames(query);
      if (this.result) this.showResult(this.result);
      if (active) this.activeIndex = this.rows.findIndex(row => row.hit === active);
      this.changeDetector.markForCheck();
    });
    try {
      const result = await this.searchService.search(projectId, query, () => {
        if (!current()) return;
        this.waitMessage = EXACT_SEARCH_WAIT;
        this.changeDetector.markForCheck();
      });
      if (!current()) return;
      this.showResult(result);
    } catch (error) {
      if (!current()) return;
      ClientLoggingService.log('ERROR', '[ProjectSearchComponent] Search failed:', error);
      this.status = 'error';
      this.errorMessage = errorText(error);
    }
    this.waitMessage = '';
    this.changeDetector.markForCheck();
  }

  /** Opens a result's page in the Preview: a text row with its words highlighted. */
  async open(row: ProjectSearchRow): Promise<void> {
    const projectId = this.projectId;
    if (!projectId || this.opening) return;
    this.opening = row;
    this.changeDetector.markForCheck();
    const startUrl = this.router.url;
    startProjectSearchJourney();
    try {
      // A plan object is labelled with its kind and name; words found on a sheet with the query.
      const label = row.hit?.entity ? entityName(row.hit.entity) : this.query.trim();
      const link = row.hit
        ? this.searchService.previewLink(projectId, row.hit, label)
        : {
            commands: previewCommands(projectId, row.target?.fileId ?? '1', row.target?.pageNumber ?? 1),
            queryParams: {},
          };
      // Even a synchronous link can race a route change triggered by the same user gesture.
      if (this.destroyed || this.router.url !== startUrl) return;
      markProjectSearchMilestone('navigation');
      const navigated = await this.router.navigate(link.commands, { queryParams: link.queryParams });
      if (!navigated) return;
      this.panelOpen = false;
      this.expanded = false;
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSearchComponent] Could not open the result:', error);
      this.status = 'error';
      this.errorMessage = 'The page could not be opened. Try again in a moment.';
    } finally {
      this.opening = null;
      this.changeDetector.markForCheck();
    }
  }

  clear(): void {
    this.query = '';
    this.generation++;
    this.resetPrefetch();
    this.clearResults();
    this.status = 'idle';
    this.panelOpen = false;
    this.activeIndex = -1;
    this.searchInput?.nativeElement.focus();
  }

  /** A click outside the field and the panel closes the panel. */
  onOutsideClick(event: MouseEvent): void {
    if (event.target instanceof Node && this.host.nativeElement.contains(event.target)) return;
    this.panelOpen = false;
  }

  onPanelDetached(): void {
    this.panelOpen = false;
  }

  /**
   * The panel's count line, such as "2 sheets · 26 text matches on 18 pages", or "26 results on 18
   * pages" when the results include plan objects.
   */
  get countText(): string {
    const textRows = this.rows.length - this.sheetRows.length - this.fileRows.length;
    const found = this.hasObjects
      ? counted(textRows, 'result', 'results')
      : counted(textRows, 'text match', 'text matches');
    const parts = [
      this.sheetTotal ? counted(this.sheetTotal, 'sheet', 'sheets') : '',
      this.fileRows.length ? counted(this.fileRows.length, 'file', 'files') : '',
      textRows ? `${found} on ${counted(this.groups.length, 'page', 'pages')}` : '',
    ];
    return parts.filter(part => part).join(' · ') || 'No results';
  }

  /** Whether the results include plan objects (Issue #3212), not only words found on the sheets. */
  get hasObjects(): boolean {
    return this.groups.some(group => group.rows.some(row => row.kind === 'object'));
  }

  /** The heading over the results found on the pages. */
  get pageResultsHeading(): string {
    return this.hasObjects ? 'On the sheets' : 'Text on sheets';
  }

  /** The id of the active row, for `aria-activedescendant`. */
  get activeRowId(): string | null {
    return this.panelOpen && this.activeIndex >= 0 ? (this.rows[this.activeIndex]?.id ?? null) : null;
  }

  /** Closes the panel and gives focus back to where it was before `/`. */
  private dismiss(): void {
    this.panelOpen = false;
    this.expanded = false;
    this.activeIndex = -1;
    const target = this.returnFocus;
    this.returnFocus = null;
    if (target && target.isConnected) {
      target.focus();
    } else {
      this.searchInput?.nativeElement.blur();
    }
  }

  private moveActive(step: number): void {
    if (this.rows.length === 0) return;
    this.openPanel();
    const next = this.activeIndex + step;
    this.activeIndex = next < 0 ? this.rows.length - 1 : next >= this.rows.length ? 0 : next;
    this.prefetchRow(this.rows[this.activeIndex]);
    this.changeDetector.detectChanges();
    const id = this.rows[this.activeIndex]?.id;
    if (id) document.getElementById(id)?.scrollIntoView?.({ block: 'nearest' });
  }

  /**
   * The current project's sheet and file names, loaded once per project. A failure is logged and
   * forgotten, so the next search tries again; results then show without the names.
   */
  private loadCatalog(): Promise<ProjectSearchCatalog | null> | null {
    const projectId = this.projectId;
    if (!projectId) return null;
    if (!this.catalogLoad) {
      const load = this.searchService.catalog(projectId).catch((error: unknown) => {
        ClientLoggingService.log('WARN', '[ProjectSearchComponent] Sheet names are not available:', error);
        if (this.catalogLoad === load) this.catalogLoad = null;
        return null;
      });
      this.catalogLoad = load;
    }
    return this.catalogLoad;
  }

  /** Opens the panel at the field's width. */
  private openPanel(): void {
    this.panelWidth = this.fieldWidth();
    this.panelOpen = true;
  }

  /** The field's width, or a readable minimum when the field is folded behind its button. */
  private fieldWidth(): number {
    const width = this.searchField?.nativeElement.getBoundingClientRect().width ?? 0;
    return Math.min(Math.max(width, MIN_PANEL_WIDTH), window.innerWidth - 16);
  }

  /** Lists the sheets and files whose names name the query. */
  private showNames(query: string): void {
    const catalog = this.catalog;
    if (!catalog) return;
    const terms = queryTerms(query);
    const sheets = matchSheets(catalog.pages, query);
    this.sheetTotal = sheets.length;
    this.sheetRows = sheets.slice(0, MAX_NAME_MATCHES).map(page => ({
      kind: 'sheet',
      icon: ROW_ICONS.sheet,
      target: { fileId: page.fileId, pageNumber: page.pageNumber },
      index: 0,
      id: '',
      segments: markTerms(sheetName(page), terms),
      pageNumber: page.pageNumber,
      fileName: catalog.fileName(page.fileId),
      detail: '',
      matchLabel: '',
      matchTooltip: '',
      diagnostic: '',
      related: false,
    }));
    this.fileRows = matchFiles(catalog.files, query)
      .slice(0, MAX_NAME_MATCHES)
      .map((file: ProjectSearchFile) => ({
        kind: 'file',
        icon: ROW_ICONS.file,
        target: { fileId: file.fileId, pageNumber: file.firstPage },
        index: 0,
        id: '',
        segments: markTerms(file.name, terms),
        detail: `${file.pages} ${file.pages === 1 ? 'page' : 'pages'}`,
        matchLabel: '',
        matchTooltip: '',
        diagnostic: '',
        related: false,
      }));
    this.numberRows();
  }

  private showResult(result: ProjectSearchResult): void {
    const catalog = this.catalog;
    const terms = queryTerms(result.query);
    this.groups = groupByPage(result.hits).map((group, groupIndex) => {
      const page = catalog?.page(group.fileId, group.pageNumber);
      // A transcript line is shown once per sheet, so two rows never read as copies (#3043).
      const shown = new Set<string>();
      return {
        key: `${group.fileId}/${group.pageNumber}`,
        headingId: `project-search-group-${groupIndex}`,
        heading: page ? sheetName(page) : displaySheetId(group.sheetId) || `Page ${group.pageNumber}`,
        pageNumber: group.pageNumber,
        fileName: catalog?.fileName(group.fileId) ?? '',
        rows: group.hits.map(hit => {
          if (hit.entity) return this.objectRow(hit, hit.entity, page?.sheetId || group.sheetId, terms);
          // The transcript's line reads better than the hit's own text, which is recognition noise
          // on a scanned sheet and, for an exact match, lower case and cut mid-word.
          const line = page ? transcriptExcerpt(page.transcript, hit, result.query) : null;
          const excerpt = line !== null && !shown.has(line) ? line : null;
          if (excerpt !== null) shown.add(excerpt);
          return {
            kind: 'text' as const,
            icon: ROW_ICONS.text,
            hit,
            index: 0,
            id: '',
            segments: markTerms(excerpt ?? hit.text, terms),
            detail: SOURCE_LABELS[excerpt ? 'transcript' : hit.source],
            matchLabel: MATCH_LABELS[hit.match],
            matchTooltip: MATCH_TOOLTIPS[hit.match],
            diagnostic: this.tabs.isPowerUserModeEnabled() ? searchRanks(hit) : '',
            related: hit.match === 'meaning',
          };
        }),
      };
    });
    this.numberRows();
    this.result = result;
    this.status = 'done';
    const likely = this.rows.find(row => row.hit);
    if (likely) {
      const generation = this.generation;
      this.prefetchCandidate = likely;
      this.prefetchTimer = setTimeout(() => {
        this.prefetchTimer = null;
        if (!this.destroyed && generation === this.generation) this.prefetchRow(likely);
      }, PREFETCH_SETTLE_DELAY_MS);
    }
  }

  /** Makes a pointed row active and opportunistically warms its page. */
  activate(row: ProjectSearchRow): void {
    this.activeIndex = row.index;
    this.prefetchRow(row);
  }

  /** Keeps no more than one parsed-page prefetch in flight for the current search generation. */
  private prefetchRow(row: ProjectSearchRow | undefined): void {
    const hit = row?.hit;
    const projectId = this.projectId;
    if (!hit || !projectId) return;
    this.prefetchCandidate = row;
    this.cancelPrefetchTimer();
    if (this.prefetchInFlight || this.prefetchChosen) return;
    const generation = this.generation;
    this.prefetchInFlight = true;
    this.prefetchChosen = true;
    void this.searchService
      .preload(projectId, hit)
      .catch(error => {
        ClientLoggingService.log('WARN', '[ProjectSearchComponent] Result prefetch failed:', error);
      })
      .finally(() => {
        // No second prefetch can have started while this flag was true, so this completion owns
        // the flag even when its search generation is now stale.
        this.prefetchInFlight = false;
        // A new search may have nominated a page while stale cache work was still finishing.
        if (!this.destroyed && generation !== this.generation && this.prefetchCandidate) {
          this.prefetchRow(this.prefetchCandidate);
        }
      });
  }

  private resetPrefetch(): void {
    this.cancelPrefetchTimer();
    // An already-started cache fill cannot be cancelled. Keep it counted until its `finally`, so
    // changing query/project never creates two concurrent result prefetches.
    this.prefetchChosen = false;
    this.prefetchCandidate = null;
  }

  private cancelPrefetchTimer(): void {
    if (this.prefetchTimer === null) return;
    clearTimeout(this.prefetchTimer);
    this.prefetchTimer = null;
  }

  /**
   * A plan object's row (Issue #3212): its kind and name, what holds it and its sheet, such as
   * "Room · Bath 1 — Unit Type D, A1.7", with the icon of its kind. Opening it highlights its
   * outline.
   */
  private objectRow(
    hit: ProjectSearchHit,
    entity: ProjectSearchEntity,
    sheetId: string,
    terms: string[]
  ): ProjectSearchRow {
    return {
      kind: 'object',
      icon: ENTITY_ICONS[entity.kind] ?? ROW_ICONS.object,
      hit,
      index: 0,
      id: '',
      segments: markTerms(entityLabel(entity, sheetId), terms),
      // The label already says what the object is; a second line would repeat it on every row.
      detail: '',
      matchLabel: MATCH_LABELS[hit.match],
      matchTooltip: MATCH_TOOLTIPS[hit.match],
      diagnostic: this.tabs.isPowerUserModeEnabled() ? searchRanks(hit) : '',
      related: hit.match === 'meaning',
    };
  }

  /** Puts every row in keyboard order (sheets, files, text) and gives each its option ID. */
  private numberRows(): void {
    this.rows = [...this.sheetRows, ...this.fileRows, ...this.groups.flatMap(group => group.rows)];
    this.rows.forEach((row, index) => {
      row.index = index;
      row.id = `project-search-result-${index}`;
    });
  }

  private clearResults(): void {
    this.result = null;
    this.groups = [];
    this.sheetRows = [];
    this.sheetTotal = 0;
    this.fileRows = [];
    this.rows = [];
    this.waitMessage = '';
  }

  private reset(): void {
    this.query = '';
    this.generation++;
    this.resetPrefetch();
    this.clearResults();
    this.catalog = null;
    this.catalogLoad = null;
    this.status = 'idle';
    this.panelOpen = false;
    this.expanded = false;
    this.activeIndex = -1;
  }
}

/** Narrowest the panel gets, so results stay readable when the field is folded on a phone. */
const MIN_PANEL_WIDTH = 360;

/** A sheet as a person names it: its ID and title, or its page number when it has neither. */
function sheetName(page: ProjectSearchPage): string {
  const name = [displaySheetId(page.sheetId), page.title].filter(part => part).join(' · ');
  return name || `Page ${page.pageNumber}`;
}

/** Which searches found a passage and at what rank, such as "words #1 · meaning #3". */
function searchRanks(hit: ProjectSearchHit): string {
  const ranks = [];
  if (hit.keywordRank) ranks.push(`words #${hit.keywordRank}`);
  if (hit.meaningRank) ranks.push(`meaning #${hit.meaningRank}`);
  return ranks.join(' · ');
}

/** "1 sheet", "3 sheets". */
function counted(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** True when a key press belongs to a field the person is typing in. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** A sentence for the panel. A gRPC error carries the server's message; anything else is generic. */
function errorText(error: unknown): string {
  const message = (error as { message?: unknown })?.message;
  return typeof message === 'string' && message.trim()
    ? `The search failed: ${message.trim()}`
    : 'The search failed. Try again in a moment.';
}
